import * as sort from "enums/sort";
import { useMemo, useState } from "react";

export type Order = "asc" | "desc";

/**
 * Generic sorting hook for any array of objects.
 *
 * Provides sorting state, sorted array, and sort request handler.
 * Generates a SortEnum for BaseTable compatibility.
 * Assumes row property names match headCell IDs exactly (1:1 mapping).
 *
 * @template T - The type of objects in the array (must be a record/object type)
 * @param rows - Array of objects to sort
 * @param defaultKey - Default field to sort by
 *
 * @example
 * ```tsx
 * const { sorted, requestSort, sortEnum } = UseSort(rows, "name");
 * ```
 */
export function UseSort<T extends Record<string, any>>(
	rows: T[],
	defaultKey: keyof T
) {
	const [order, setOrder] = useState<Order>("asc");
	const [orderBy, setOrderBy] = useState<keyof T>(defaultKey);

	const sorted = useMemo(() => {
		const getComparable = (obj: T, key: keyof T): string => {
			const value = obj[key] as unknown;
			if (Array.isArray(value)) return value.join(", ");
			return (value ?? "") as string;
		};
		return [...rows].sort((firstItem, secondItem) => {
			const firstValue = getComparable(firstItem, orderBy).toLowerCase();
			const secondValue = getComparable(secondItem, orderBy).toLowerCase();
			const comparison = firstValue < secondValue ? -1 : firstValue > secondValue ? 1 : 0;
			return order === "asc" ? comparison : -comparison;
		});
	}, [rows, order, orderBy]);

	const requestSort = (key: keyof T) => {
		setOrder((prev) => (orderBy === key && prev === "asc" ? "desc" : "asc"));
		setOrderBy(key);
	};

	const sortEnum = useMemo(() => {
		// Property names match headCell IDs exactly (1:1 mapping)
		const sortField = orderBy as string;
		return order === "asc" ? sort.Asc(sortField) : sort.Desc(sortField);
	}, [order, orderBy]);

	return {
		order,
		orderBy,
		sorted,
		requestSort,
		sortEnum,
	};
}
